<template>
  <div
    class="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4 lg:p-6 xl:p-8"
  >
    <!-- Navegación entre pantallas públicas -->
    <div v-if="modoPublico" style="position:fixed;top:10px;right:10px;z-index:9999">
      <a href="/pantalla-cocina-publica" class="btn btn-sm btn-dark me-1">Cocina</a>
      <a href="/pantalla-barra-publica" class="btn btn-sm btn-dark">Barra</a>
    </div>

    <!-- Aviso de sonido bloqueado por el navegador (autoplay policy) -->
    <button
      v-if="!sonidoActivado"
      class="aviso-sonido"
      type="button"
      @click="activarSonido"
    >
      🔇 Toca aquí para activar el sonido de los pedidos
    </button>

    <!-- Header  -->
    <div class="mb-4 lg:mb-6 2xl:mb-8">
      <h1
        class="text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold text-slate-800 dark:text-white mb-2 flex items-center gap-3"
      >
        <span>Pedidos en Cocina</span>
      </h1>
      <p class="text-lg lg:text-xl 2xl:text-2xl text-slate-600 dark:text-slate-400">
        Área de Producción
      </p>
    </div>

    <!-- Sin pedidos -->
    <div
      v-if="pedidosConColor.length === 0"
      class="bg-slate-200 dark:bg-slate-700 rounded-2xl p-8 lg:p-12 text-center"
    >
      <div class="text-6xl lg:text-7xl mb-4">✨</div>
      <p
        class="text-2xl lg:text-3xl font-medium text-slate-700 dark:text-slate-300"
      >
        No hay pedidos pendientes
      </p>
    </div>

    <!-- Grid de pedidos -->
    <div ref="scrollContainer" class="cards-scroll">
      <div class="cards-grid">
        <div
          v-for="pedido in pedidosConColor"
          :key="pedido.id"
          class="pedido-card group"
          :class="pedido.color"
        >
          <!-- Header de la tarjeta -->
          <div class="card-header">
            <div class="flex items-center gap-2 lg:gap-3">
              <div class="bg-white/40 dark:bg-black/30 rounded-full p-2 lg:p-3 2xl:p-4 shadow-lg">
                <svg
                  class="w-6 h-6 lg:w-8 lg:h-8 2xl:w-10 2xl:h-10 drop-shadow-md"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"
                  ></path>
                </svg>
              </div>
              <div class="flex items-center gap-2 lg:gap-3">
                <div>
                  <p class="text-xs lg:text-sm font-semibold text-slate-900 dark:text-white drop-shadow-md">Mesa</p>
                  <p
                    class="text-3xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-black leading-none text-slate-900 dark:text-white drop-shadow-lg"
                  >
                    {{ pedido.mesa }}
                  </p>
                </div>
                <!-- Mesero en la misma línea -->
                <div v-if="pedido.mesero" class="mt-4">
                  <p class="text-sm lg:text-base xl:text-lg font-extrabold text-slate-900 dark:text-white drop-shadow-md bg-white/30 dark:bg-black/30 px-2 lg:px-3 py-1 lg:py-1.5 rounded-lg border border-white/40">
                    {{ pedido.mesero }}
                  </p>
                </div>
              </div>
            </div>
            <!-- Cronómetro -->
            <div
              class="inline-flex items-center gap-1.5 lg:gap-2 bg-slate-900/90 dark:bg-black/60 text-white px-3 lg:px-4 2xl:px-6 py-1.5 lg:py-2 2xl:py-3 rounded-xl backdrop-blur-sm shadow-xl border border-white/20"
            >
              <span class="text-xl lg:text-2xl 2xl:text-3xl">⏱</span>
              <span
                class="text-xl lg:text-2xl xl:text-3xl 2xl:text-4xl font-mono font-bold"
                >{{ getTimer(pedido) }}</span
              >
            </div>
          </div>

          <!-- Cuerpo de la tarjeta -->
          <div class="card-body">
            <div
              :class="[
                pedido.detalles.length > 5
                  ? 'grid grid-cols-2 gap-2 lg:gap-2.5 2xl:gap-3'
                  : 'space-y-2 lg:space-y-2.5 2xl:space-y-3'
              ]"
            >
              <div
                v-for="det in pedido.detalles"
                :key="det.id_detalle_pedido"
                class="bg-white/60 dark:bg-white/15 rounded-lg p-2.5 lg:p-3 2xl:p-4 backdrop-blur-sm border-2 border-white/70 dark:border-white/25 shadow-md"
              >
                <p
                  class="text-base lg:text-lg xl:text-xl 2xl:text-2xl font-extrabold text-slate-900 dark:text-white mb-1.5 drop-shadow-sm"
                >
                  {{ det.platillo }}
                </p>

                <!-- Ingredientes quitados -->
                <div
                  v-if="det.ingredientes_no_quiere.length"
                  class="flex flex-wrap gap-2 mt-3"
                >
                  <span
                    v-for="ing in det.ingredientes_no_quiere"
                    :key="ing"
                    class="inline-flex items-center gap-1 bg-red-500 text-white px-3 py-1.5 lg:px-4 lg:py-2 rounded-lg text-sm lg:text-base font-semibold shadow-lg"
                  >
                    <svg
                      class="w-4 h-4 lg:w-5 lg:h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clip-rule="evenodd"
                      ></path>
                    </svg>
                    Sin {{ ing }}
                  </span>
                </div>

                <!-- Opciones y Elecciones -->
                <div v-if="det.grupos.length" class="flex flex-wrap gap-2 mt-3">
                  <span
                    v-for="(g, i) in det.grupos"
                    :key="i"
                    :class="[
                      'inline-flex items-center gap-1 px-3 py-1.5 lg:px-4 lg:py-2 rounded-lg text-sm lg:text-base font-semibold shadow-lg',
                      (g.precio_extra && parseFloat(g.precio_extra) > 0)
                        ? 'bg-purple-600 text-white'
                        : 'bg-blue-500 text-white'
                    ]"
                  >
                    <svg
                      class="w-4 h-4 lg:w-5 lg:h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clip-rule="evenodd"
                      ></path>
                    </svg>
                    <span v-if="g.precio_extra && parseFloat(g.precio_extra) > 0" class="font-bold">Extra:</span>
                    {{ g.opcion }}
                    <span
                      v-if="g.precio_extra && parseFloat(g.precio_extra) > 0 && g.cantidad > 1"
                      class="ml-1 bg-white/30 px-2 py-0.5 rounded font-bold"
                      >x{{ g.cantidad }}</span
                    >
                  </span>
                </div>
              </div>
            </div>

            <!-- Notas del mesero -->
            <div v-if="pedido.notas" class="mt-3 bg-yellow-300/80 dark:bg-yellow-500/25 rounded-xl p-3 border-2 border-yellow-400/60 dark:border-yellow-500/50">
              <div class="flex items-start gap-2">
                <span class="text-yellow-800 dark:text-yellow-300 text-lg leading-none mt-0.5">📝</span>
                <div>
                  <p class="text-xs font-bold text-yellow-900 dark:text-yellow-200 uppercase tracking-wide mb-0.5">Notas</p>
                  <p class="text-sm lg:text-base font-semibold text-yellow-900 dark:text-yellow-100 leading-snug">{{ pedido.notas }}</p>
                </div>
              </div>
            </div>

            <!-- Botones de acción -->
            <div class="flex gap-2 lg:gap-2.5 2xl:gap-3 mt-3 lg:mt-3.5 2xl:mt-4">
              <button
                class="flex-1 relative overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 hover:from-slate-50 hover:to-slate-100 dark:hover:from-slate-600 dark:hover:to-slate-700 text-slate-800 dark:text-white font-bold py-3 lg:py-3.5 2xl:py-5 px-3 lg:px-4 rounded-xl text-sm lg:text-base 2xl:text-xl transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-[1.03] flex items-center justify-center gap-1.5 lg:gap-2 border-2 border-slate-300/50 dark:border-slate-600/50 hover:border-slate-400 dark:hover:border-slate-500 group"
                @click="imprimirManual(pedido)"
              >
                <svg class="w-5 h-5 lg:w-6 lg:h-6 2xl:w-7 2xl:h-7 transition-transform group-hover:scale-110" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clip-rule="evenodd"></path>
                </svg>
                <span class="font-extrabold">Imprimir</span>
              </button>
              <button
                class="flex-1 relative overflow-hidden bg-gradient-to-br from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white font-bold py-3 lg:py-3.5 2xl:py-5 px-3 lg:px-4 rounded-xl text-sm lg:text-base 2xl:text-xl transition-all duration-300 shadow-lg hover:shadow-2xl hover:shadow-green-500/50 hover:scale-[1.03] flex items-center justify-center gap-1.5 lg:gap-2 border-2 border-green-400/50 hover:border-green-300 group"
                @click="marcarPedidoComoCompletado(pedido)"
              >
                <svg class="w-5 h-5 lg:w-6 lg:h-6 2xl:w-7 2xl:h-7 transition-transform group-hover:scale-110 group-hover:rotate-12" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                </svg>
                <span class="font-extrabold">Completado</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_URL, PANTALLA_TOKEN } from "@/config/config";
import { imprimirComandaCocina } from "@/utils/impresion";
import {
  SONIDOS,
  crearAudio,
  desbloquearAudios,
  reproducir,
} from "@/utils/sonidos";

export default {
  name: "PantallaCocina",
  data() {
    return {
      pedidos: [],
      now: new Date(),

      // 🔔 Sonido de nuevo pedido
      sonidoNuevoPedido: crearAudio(SONIDOS.nuevoPedido),

      // 🔔 Sonido de pedido completado
      sonidoPedidoListo: crearAudio(SONIDOS.pedidoListo),

      // El navegador bloquea el audio hasta que alguien toque la pantalla.
      sonidoActivado: false,
      intervaloPedidos: null,
      intervaloReloj: null,

      totalAnterior: 0, // para comparar pedidos nuevos
      pedidosIdsAnteriores: [], // IDs de pedidos anteriores
      impresosGlobal: [], // IDs de pedidos impresos desde el backend (compartido entre browsers)
      pedidosVistosLocal: new Set(), // IDs vistos por ESTE browser (para sonido local)
      primerasCarga: true, // Evita sonido al cargar pedidos existentes al iniciar
    };
  },

  computed: {
    modoPublico() {
      return this.$route.path.includes('-publica');
    },
    pedidosConColor() {
      return this.pedidos.map((p) => {
        let color = "card-green";

        if (p.detalles.length) {
          const fechaInicio = new Date(p.detalles[0].fecha_creacion);
          const diffSec = Math.floor((this.now - fechaInicio) / 1000);

          if (diffSec >= 600) color = "card-red"; // 10 minutos
          else if (diffSec >= 300) color = "card-yellow"; // 5 minutos
        }

        // Color azul para extras (opcional)
        const esExtra = p.detalles.some((det) => det.categoria === "Extras");
        if (esExtra) color = "card-blue";

        return { ...p, color };
      })
      // Ordenar más antiguo → más nuevo (primeros en entrar primero)
      .sort((a, b) => {
        const fechaA = a.detalles.length
          ? new Date(a.detalles[0].fecha_creacion)
          : new Date();
        const fechaB = b.detalles.length
          ? new Date(b.detalles[0].fecha_creacion)
          : new Date();
        return fechaA - fechaB;
      });
    },
  },

  methods: {
    // Se llama desde el botón del aviso y desde el primer gesto que reciba la
    // pantalla (click, tap o tecla), que es lo que exige el navegador para
    // permitir reproducir audio después.
    async activarSonido() {
      this.sonidoActivado = await desbloquearAudios([
        this.sonidoNuevoPedido,
        this.sonidoPedidoListo,
      ]);
      if (this.sonidoActivado) this.quitarListenersGesto();
    },
    quitarListenersGesto() {
      ["pointerdown", "keydown", "touchstart"].forEach((evt) =>
        document.removeEventListener(evt, this.activarSonido),
      );
    },
    // Si el navegador vuelve a bloquear el audio, se muestra el aviso otra vez
    // en lugar de quedarse mudo sin que nadie se entere.
    async reproducirSonido(audio) {
      if (!(await reproducir(audio))) this.sonidoActivado = false;
    },
    async cargarImpresosGlobal() {
      // /impresion-comanda exige rol (1,7,9) y la pantalla pública no manda
      // X-User-Id: llamarla devuelve 401. Mismo criterio que imprimirManual.
      if (this.modoPublico) {
        this.impresosGlobal = [];
        return;
      }
      try {
        const { data } = await axios.get(`${API_URL}/impresion-comanda`, {
          params: { tipo: "cocina" },
        });
        this.impresosGlobal = data.impresos || [];
      } catch (err) {
        console.error("Error cargando impresos globales:", err);
        this.impresosGlobal = [];
      }
    },
    async registrarImpresion(pedido) {
      // En modo público se imprime sin correlativo (no hay auth para registrarlo).
      if (this.modoPublico) {
        const idUnico = `${pedido.id}_${pedido.fecha_creacion}`;
        if (!this.impresosGlobal.includes(idUnico)) {
          this.impresosGlobal.push(idUnico);
        }
        return null;
      }
      try {
        const response = await axios.post(`${API_URL}/impresion-comanda`, {
          IdPedido: pedido.id,
          TipoComanda: "cocina",
          FechaCreacionPedido: pedido.fecha_creacion,
        });
        // Agregar a la lista local para evitar reimprimir en este ciclo
        const idUnico = `${pedido.id}_${pedido.fecha_creacion}`;
        if (!this.impresosGlobal.includes(idUnico)) {
          this.impresosGlobal.push(idUnico);
        }
        // Retornar el correlativo para usarlo en la impresión
        return response.data.correlativo || null;
      } catch (err) {
        console.error("Error registrando impresión:", err);
        return null;
      }
    },
    async imprimirManual(pedido) {
      // En modo público no hay auth — imprimir directo sin registrar correlativo
      let correlativo = null;
      if (!this.modoPublico) {
        try {
          const { data: impData } = await axios.get(
            `${API_URL}/impresion-comanda/por-pedido/${pedido.id}`,
          );
          correlativo = impData.correlativo;
        } catch (e) {
          // sin correlativo previo
        }

        if (!correlativo) {
          try {
            const { data: regData } = await axios.post(
              `${API_URL}/impresion-comanda`,
              {
                IdPedido: pedido.id,
                TipoComanda: "cocina",
                FechaCreacionPedido: pedido.fecha_creacion,
              },
            );
            correlativo = regData.correlativo || null;
          } catch (e) {
            // no se pudo registrar
          }
        }
      }

      const mesaInfo = { Numero: pedido.mesa };
      const detallesParaImprimir = pedido.detalles.map((det) => ({
        cantidad: det.cantidad || 1,
        nombre_platillo: det.platillo,
        cuenta_nombre: det.cuenta_nombre || "Sin cuenta",
        opciones: det.grupos.map((g) => ({
          nombre: g.opcion,
          precio_extra: g.precio_extra,
          cantidad: g.cantidad || 1,
        })),
        ingredientes_no_quiere: det.ingredientes_no_quiere.map((ing) => ({
          nombre: ing,
        })),
        notas: pedido.notas || "",
        area_produccion: "Cocina",
      }));
      const pedidoParaImprimir = { IdPedido: pedido.id };
      imprimirComandaCocina(
        pedidoParaImprimir,
        detallesParaImprimir,
        mesaInfo,
        pedido.mesero,
        correlativo,
      );
    },
    async cargarPedidos() {
      try {
        const url = this.modoPublico
          ? `${API_URL}/listar-pedidos/cocina-publico?token=${PANTALLA_TOKEN}`
          : `${API_URL}/listar-pedidos/cocina`;
        const { data } = await axios.get(url);
        const idsActuales = data.pedidos.map(
          (p) => `${p.id}_${p.fecha_creacion}`,
        );

        // Detectar pedidos no vistos por ESTE browser (para sonido e impresión)
        const pedidosNoVistos = data.pedidos.filter(
          (p) => !this.pedidosVistosLocal.has(`${p.id}_${p.fecha_creacion}`)
        );
        data.pedidos.forEach((p) =>
          this.pedidosVistosLocal.add(`${p.id}_${p.fecha_creacion}`)
        );

        // Actualizar la UI con TODOS los pedidos
        this.pedidos = data.pedidos.map((pedido) => ({
          ...pedido,
          detalles: pedido.detalles.map((det) => ({
            ...det,
            grupos: Array.isArray(det.grupos)
              ? det.grupos
              : Object.values(det.grupos || {}),
          })),
        }));

        // Sonido + Impresión para pedidos nuevos en ESTE browser
        if (!this.primerasCarga && pedidosNoVistos.length > 0) {
          // 🔊 Sonido
          this.reproducirSonido(this.sonidoNuevoPedido);

          // 🖨️ Usar pedidos ya normalizados y el mismo código del botón Imprimir
          const idsNuevos = new Set(
            pedidosNoVistos.map((p) => `${p.id}_${p.fecha_creacion}`)
          );
          const pedidosNuevosNorm = this.pedidos.filter((p) =>
            idsNuevos.has(`${p.id}_${p.fecha_creacion}`)
          );

          pedidosNuevosNorm.forEach((pedidoNuevo, index) => {
            setTimeout(() => {
              this.imprimirManual(pedidoNuevo);
            }, 300 * (index + 1));
          });
        }
        this.primerasCarga = false;

        this.pedidosIdsAnteriores = idsActuales;
        this.totalAnterior = data.pedidos.length;
      } catch (err) {
        console.error("Error cargando pedidos cocina:", err);
      }
    },

    async marcarPedidoComoCompletado(pedido) {
      try {
        const url = this.modoPublico
          ? `${API_URL}/pedidos-mesa/marcar-completado-cocina-publico?token=${PANTALLA_TOKEN}`
          : `${API_URL}/pedidos-mesa/marcar-completado-cocina`;
        await axios.put(url, {
          detalles: pedido.detalles.map((d) => d.id_detalle_pedido),
        });

        // Reproducir sonido de pedido listo
        this.reproducirSonido(this.sonidoPedidoListo);

        this.cargarPedidos();
      } catch (err) {
        console.error("Error marcando pedido:", err);
      }
    },

    getTimer(pedido) {
      if (!pedido.detalles.length) return "00:00";
      const fechaInicio = new Date(pedido.detalles[0].fecha_creacion);
      if (isNaN(fechaInicio.getTime())) return "00:00";
      const diffMs = this.now - fechaInicio;
      if (diffMs < 0) return "00:00";
      const diffSec = Math.floor(diffMs / 1000);
      const min = Math.floor(diffSec / 60);
      const sec = diffSec % 60;
      return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
    },
  },

  mounted() {
    this.cargarPedidos();

    // Intento silencioso: si el navegador ya confía en el sitio, el audio queda
    // listo sin pedir nada. Si no, cualquier gesto sobre la pantalla lo activa.
    this.activarSonido();
    ["pointerdown", "keydown", "touchstart"].forEach((evt) =>
      document.addEventListener(evt, this.activarSonido, { passive: true }),
    );

    this.intervaloPedidos = setInterval(this.cargarPedidos, 3000); // refrescar cada 3s

    this.intervaloReloj = setInterval(() => {
      this.now = new Date();
    }, 1000);
  },

  beforeUnmount() {
    // Sin esto los intervalos sobreviven al cambiar de pantalla: la vista vieja
    // sigue consultando pedidos, sonando e imprimiendo desde atrás.
    clearInterval(this.intervaloPedidos);
    clearInterval(this.intervaloReloj);
    this.quitarListenersGesto();
  },
};
</script>

<style scoped>
.aviso-sonido {
  position: fixed;
  bottom: 18px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  border: none;
  border-radius: 999px;
  padding: 14px 28px;
  font-size: 1.25rem;
  font-weight: 700;
  color: #fff;
  background: #dc2626;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  cursor: pointer;
  animation: pulso-sonido 1.6s ease-in-out infinite;
}

@keyframes pulso-sonido {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}

@media (prefers-reduced-motion: reduce) {
  .aviso-sonido {
    animation: none;
  }
}

.cards-scroll {
  height: calc(100vh - 180px);
  overflow-y: auto;
  overflow-x: hidden;
}

.cards-scroll::-webkit-scrollbar {
  width: 12px;
}

.cards-scroll::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 10px;
}

.cards-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 10px;
}

.cards-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.5);
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
}

/* Desktop normal - moderado */
@media (min-width: 1024px) {
  .cards-grid {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 1.25rem;
  }
}

/* Desktop grande */
@media (min-width: 1280px) {
  .cards-grid {
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
    gap: 1.5rem;
  }
}

/* Pantallas muy grandes - 2XL */
@media (min-width: 1536px) {
  .cards-grid {
    grid-template-columns: repeat(auto-fill, minmax(480px, 1fr));
    gap: 2rem;
  }
}

/* Televisiones y 4K */
@media (min-width: 1920px) {
  .cards-grid {
    grid-template-columns: repeat(auto-fill, minmax(550px, 1fr));
    gap: 2.5rem;
  }
}

.pedido-card {
  border-radius: 1.5rem;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
}

.pedido-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
}

/* Colores dinámicos con gradientes */
.pedido-card.card-green {
  background: linear-gradient(135deg, #10b981 0%, #34d399 100%);
  color: white;
}

.pedido-card.card-yellow {
  background: linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%);
  color: #78350f;
}

.pedido-card.card-red {
  background: linear-gradient(135deg, #ef4444 0%, #f87171 100%);
  color: white;
}

.pedido-card.card-blue {
  background: linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%);
  color: white;
}

.card-header {
  padding: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid rgba(255, 255, 255, 0.4);
}

@media (min-width: 1024px) {
  .card-header {
    padding: 0.875rem;
  }
}

@media (min-width: 1536px) {
  .card-header {
    padding: 1.25rem;
  }
}

.card-body {
  padding: 0.75rem;
}

@media (min-width: 1024px) {
  .card-body {
    padding: 0.875rem;
  }
}

@media (min-width: 1536px) {
  .card-body {
    padding: 1.25rem;
  }
}

/* Animación de entrada */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.pedido-card {
  animation: slideIn 0.4s ease-out;
}
</style>
